import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { functions, type HttpsCallableResult } from 'firebaseApp';
import {
  addUserAddress,
  deleteUserAddress,
  getUserAddresses,
} from '../../firebase/userFirestore';
import { accountCleared } from '../actions';
import type { RootState } from '../store';

type StateAddresses = {
  id: string;
  placeId?: string;
  data: Pagcomanda.Address;
};

interface CalculateDistancePayload {
  Address: string;
  VenueId: string;
}

interface CalculateDistanceResponse {
  distanceInMeters: number;
  taxValue: number;
  routes?: Array<{
    legs: Array<{
      distance: { value: number };
    }>;
  }>;
}

type CalculateDistanceResult = HttpsCallableResult<CalculateDistanceResponse>;

interface UserState {
  isAddAddressModalOpen: boolean;
  savingAddress: 'idle' | 'pending' | 'succeeded' | 'failed';

  loadingAddresses: 'idle' | 'pending' | 'succeeded' | 'failed';
  addresses: StateAddresses[];

  deliveryAddress: null | StateAddresses;
  deliveryVenueId: string | null;
  calculatingFreight: 'idle' | 'pending' | 'succeeded' | 'failed';
  freightError: null | string;
  freightAmount: number;

  profile: null | Pagcomanda.User;
  loadingProfile: 'idle' | 'pending' | 'succeeded' | 'failed';
  savingProfile: 'idle' | 'pending' | 'succeeded' | 'failed';
  isProfileDataModalOpen: boolean;
  isProfileDataRequiredForOrder: boolean;
}

const initialState: UserState = {
  isAddAddressModalOpen: false,
  savingAddress: 'idle',
  loadingAddresses: 'idle',
  addresses: [],
  deliveryAddress: null,
  deliveryVenueId: null,
  calculatingFreight: 'idle',
  freightError: null,
  freightAmount: 0,
  loadingProfile: 'idle',
  profile: null,
  savingProfile: 'idle',
  isProfileDataModalOpen: false,
  isProfileDataRequiredForOrder: false,
};

export const loadAddresses = createAsyncThunk(
  'user/loadAddresses',
  async () => getUserAddresses(),
);

export const setDeliveryAddress = createAsyncThunk(
  'user/setDeliveryAddress',
  async (address: StateAddresses, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    const { deliveryVenueId } = state.user;

    if (!deliveryVenueId) throw new Error('O estabelecimento não foi informado.');

    const fnCalculateDistance = functions.httpsCallable<
      CalculateDistancePayload,
      CalculateDistanceResponse
    >('calculateDistance');

    const result: CalculateDistanceResult = await fnCalculateDistance({
      Address: `${address.data.street}, ${address.data.number}, ${address.data.district}, ${address.data.cityName}, ${address.data.stateInitials}, ${address.data.countryName}`,
      VenueId: deliveryVenueId,
    });

    return result;
  },
);

export const addDeliveryAddress = createAsyncThunk(
  'user/addDeliveryAddress',
  async (address: Pagcomanda.Address, thunkApi) => {
    const insertedId = await addUserAddress(address);
    if (!insertedId) return null;

    await thunkApi.dispatch(loadAddresses());

    const state = thunkApi.getState() as RootState;
    const insertedAddress = state.user.addresses.find((i) => i.id === insertedId);
    if (insertedAddress) {
      await thunkApi.dispatch(setDeliveryAddress(insertedAddress));
    }

    return insertedId;
  },
);

export const deleteAddress = createAsyncThunk(
  'user/deleteAddress',
  async (id: string, thunkApi) => {
    const deleted = await deleteUserAddress(id);
    if (!deleted) return false;

    thunkApi.dispatch(loadAddresses());

    return true;
  },
);

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    openAddAddressModal: (state) => ({
      ...state,
      isAddAddressModalOpen: true,
    }),
    closeAddAddressModal: (state) => ({
      ...state,
      isAddAddressModalOpen: false,
    }),
    resetDeliveryAddress: (state) => ({
      ...state,
      deliveryAddress: null,
      calculatingFreight: 'idle',
      freightError: null,
      freightAmount: 0,
    }),
    restoreDeliveryState: (state, action: PayloadAction<{
      address: StateAddresses | null;
      fee: number;
    }>) => ({
      ...state,
      deliveryAddress: action.payload.address,
      calculatingFreight: action.payload.address ? 'succeeded' : 'idle',
      freightError: null,
      freightAmount: action.payload.fee,
    }),
    setCalculatedFreight: (state, action: PayloadAction<number>) => ({
      ...state,
      calculatingFreight: 'succeeded',
      freightError: null,
      freightAmount: action.payload,
    }),
    setDeliveryVenueId: (state, action: PayloadAction<string | null>) => ({
      ...state,
      deliveryVenueId: action.payload,
    }),
    logout: (state) => ({
      ...state,
      ...initialState,
    }),

    openProfileDataModal: (state) => ({
      ...state,
      isProfileDataModalOpen: true,
      isProfileDataRequiredForOrder: false,
      savingAddress: 'idle',
    }),
    requireProfileDataForOrder: (state) => ({
      ...state,
      isProfileDataModalOpen: true,
      isProfileDataRequiredForOrder: true,
    }),
    closeProfileDataModal: (state) => ({
      ...state,
      isProfileDataModalOpen: false,
    }),
    completeProfileDataModal: (state) => ({
      ...state,
      isProfileDataModalOpen: false,
      isProfileDataRequiredForOrder: false,
    }),
  },
  extraReducers: (builder) => {
    builder.addCase(accountCleared, () => initialState);
    builder.addCase(addDeliveryAddress.pending, (state) => ({ ...state, savingAddress: 'pending' }));
    builder.addCase(
      addDeliveryAddress.fulfilled,
      (state) => ({ ...state, savingAddress: 'succeeded', isAddAddressModalOpen: false }),
    );
    builder.addCase(addDeliveryAddress.rejected, (state) => ({ ...state, savingAddress: 'failed' }));

    builder.addCase(setDeliveryAddress.pending, (state) => ({
      ...state, calculatingFreight: 'pending', freightAmount: 0, freightError: null,
    }));
    builder.addCase(
      setDeliveryAddress.fulfilled,
      (state, action) => ({
        ...state,
        calculatingFreight: 'succeeded',
        deliveryAddress: action.meta.arg,
        freightAmount: action.payload.data.taxValue,
      }),
    );
    builder.addCase(setDeliveryAddress.rejected, (state, action) => ({
      ...state,
      calculatingFreight: 'failed',
      deliveryAddress: null,
      freightAmount: 0,
      freightError: action.error.message ?? 'Erro ao calcular. Tente novamente.',
    }));

    builder.addCase(
      loadAddresses.pending,
      (state) => ({
        ...state,
        loadingAddresses: 'pending',
      }),
    );
    builder.addCase(
      loadAddresses.fulfilled,
      (state, action) => ({
        ...state,
        addresses: action.payload,
        loadingAddresses: 'succeeded',
      }),
    );
    builder.addCase(
      loadAddresses.rejected,
      (state) => ({
        ...state,
        loadingAddresses: 'failed',
      }),
    );
  },
});

export const {
  openAddAddressModal,
  closeAddAddressModal,
  resetDeliveryAddress,
  restoreDeliveryState,
  setCalculatedFreight,
  setDeliveryVenueId,
  openProfileDataModal,
  requireProfileDataForOrder,
  closeProfileDataModal,
  completeProfileDataModal,
  logout,
} = userSlice.actions;

export const selectUser = (state: RootState) => state.user;
export const selectIsAddAddressModalOpen = (state: RootState) => state.user.isAddAddressModalOpen;

export const selectProfile = (state: RootState) => state.user.profile;
export const selectIsProfileDataModalOpen = (state: RootState) => state.user.isProfileDataModalOpen;
export const selectIsProfileDataRequiredForOrder = (
  state: RootState,
) => state.user.isProfileDataRequiredForOrder;
export const selectIsSavingProfile = (state: RootState) => state.user.savingProfile;

export const selectLoadingAddresses = (state: RootState) => state.user.loadingAddresses;
export const selectUserAddresses = (state: RootState) => state.user.addresses;

export const selectSavingAddress = (state: RootState) => state.user.savingAddress;

export const selectIsCalculatingFreight = (state: RootState) => state.user.calculatingFreight;
export const selectDeliveryAddress = (state: RootState) => state.user.deliveryAddress;
export const selectFreightError = (state: RootState) => state.user.freightError;
export const selectFreightAmount = (state: RootState) => state.user.freightAmount;

export default userSlice.reducer;
